import {
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  Box,
} from '@mantine/core';
import {
  noteMetadataMdmLevelValues,
  noteMetadataPatientStatusValues,
  noteMetadataPayerPlanTypeValues,
  type NoteMetadataSnapshot,
} from '@/schemas/note-metadata.schema';
import { enrichBillingCodesWithCpt } from '../noteMetadataMapping';
import type { AppointmentSessionType } from '@/pages/calendar/appointmentTypeCatalog';

function currentCptCodes(metadata: NoteMetadataSnapshot): string[] {
  return (metadata.billingCodes ?? [])
    .map((r) => r.code?.trim().toUpperCase())
    .filter((c): c is string => Boolean(c) && c !== 'UNSPECIFIED');
}

function codesInclude(metadata: NoteMetadataSnapshot, codes: string[]): boolean {
  const selected = currentCptCodes(metadata);
  return codes.some((c) => selected.includes(c));
}

const EM_CODES = ['99202', '99203', '99204', '99205', '99212', '99213', '99214', '99215'];
const CRISIS_CODES = ['90839', '90840'];

export interface EncounterScrubberMetadataFieldsProps {
  metadata: NoteMetadataSnapshot;
  onChange: (metadata: NoteMetadataSnapshot) => void;
  readOnly?: boolean;
  serviceTypeOptions?: AppointmentSessionType[];
  validationErrors?: Record<string, string>;
}

const BOOL_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

function boolSelectValue(value: boolean | undefined): string | null {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return null;
}

function parseBoolSelect(val: string | null): boolean | undefined {
  if (val === 'true') return true;
  if (val === 'false') return false;
  return undefined;
}

export function EncounterScrubberMetadataFields({
  metadata,
  onChange,
  readOnly = false,
  serviceTypeOptions = [],
  validationErrors = {},
}: EncounterScrubberMetadataFieldsProps): JSX.Element | null {
  const enriched =
    serviceTypeOptions.length > 0
      ? enrichBillingCodesWithCpt(metadata, serviceTypeOptions)
      : metadata;
  const selected = currentCptCodes(enriched);

  if (selected.length === 0) return null;

  const showEm = codesInclude(enriched, EM_CODES);
  const show90791 = selected.includes('90791');
  const show90792 = selected.includes('90792');
  const showCrisis = codesInclude(enriched, CRISIS_CODES);
  const show90833 = selected.includes('90833');
  const show96127 = selected.includes('96127');
  const show96146 = selected.includes('96146');
  const show90785 = selected.includes('90785');
  const showLegacyConsult = selected.includes('99241');

  const patch = (partial: Partial<NoteMetadataSnapshot>) => {
    onChange({ ...metadata, ...partial });
  };

  const boolField = (
    key: keyof NoteMetadataSnapshot,
    label: string,
    visible: boolean,
  ): JSX.Element | null => {
    if (!visible) return null;
    const value = metadata[key] as boolean | undefined;
    if (readOnly) {
      return (
        <Stack gap={2}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            {label}
          </Text>
          <Text size="sm">{value === true ? 'Yes' : value === false ? 'No' : '—'}</Text>
        </Stack>
      );
    }
    return (
      <Select
        label={label}
        data={BOOL_OPTIONS}
        value={boolSelectValue(value)}
        error={validationErrors[key as string]}
        onChange={(val) => patch({ [key]: parseBoolSelect(val) })}
        clearable
        placeholder="Select"
      />
    );
  };

  return (
    <Box data-note-field="billing-evaluation">
    <Stack gap="sm" mt="md">
      <Title order={5}>Billing evaluation</Title>
      <Text size="xs" c="dimmed">
        Required for billing review based on selected CPT codes. Issues appear here as you edit and are rechecked before signing.
      </Text>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        {showEm && (
          <>
            {readOnly ? (
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Patient status
                </Text>
                <Text size="sm">{metadata.patientStatus ?? '—'}</Text>
              </Stack>
            ) : (
              <Select
                label="Patient status"
                data={noteMetadataPatientStatusValues.map((v) => ({ value: v, label: v }))}
                value={metadata.patientStatus ?? null}
                error={validationErrors.patientStatus}
                onChange={(val) =>
                  patch({ patientStatus: (val as NoteMetadataSnapshot['patientStatus']) ?? undefined })
                }
                clearable
                placeholder="New or established"
              />
            )}
            {readOnly ? (
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  MDM level
                </Text>
                <Text size="sm">{metadata.mdmLevel ?? '—'}</Text>
              </Stack>
            ) : (
              <Select
                label="MDM level"
                data={noteMetadataMdmLevelValues.map((v) => ({ value: v, label: v }))}
                value={metadata.mdmLevel ?? null}
                error={validationErrors.mdmLevel}
                onChange={(val) =>
                  patch({ mdmLevel: (val as NoteMetadataSnapshot['mdmLevel']) ?? undefined })
                }
                clearable
                placeholder="Straightforward to high"
              />
            )}
          </>
        )}

        {boolField(
          'firstSessionWithClientOrCase',
          'First session with client/case',
          show90791,
        )}
        {boolField(
          'newTherapistFirstSessionWithClientOrCase',
          'New therapist first session with client/case',
          show90791,
        )}
        {boolField(
          'diagnosticEvaluationDocumentationPresent',
          'Diagnostic evaluation documentation present',
          show90792,
        )}
        {boolField('crisisDocumentationPresent', 'Crisis documentation present', showCrisis)}
        {boolField(
          'assessmentPerformedSameDateAsSession',
          'Assessment performed same date as session',
          show96127,
        )}
        {boolField('psychotherapyAddOn', 'Psychotherapy add-on documented', show90833)}
        {boolField(
          'interactiveComplexityFactorDocumented',
          'Interactive complexity factor documented',
          show90785,
        )}
        {boolField('legacyConsultCodeEnabled', 'Legacy consult code enabled', showLegacyConsult)}
        {boolField('payerAllowsConsultCodes', 'Payer allows consult codes', showLegacyConsult)}

        {show96146 &&
          (readOnly ? (
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Payer plan type
              </Text>
              <Text size="sm">{metadata.payerPlanType ?? '—'}</Text>
            </Stack>
          ) : (
            <Select
              label="Payer plan type"
              data={noteMetadataPayerPlanTypeValues.map((v) => ({ value: v, label: v }))}
              value={metadata.payerPlanType ?? null}
              error={validationErrors.payerPlanType}
              onChange={(val) =>
                patch({ payerPlanType: (val as NoteMetadataSnapshot['payerPlanType']) ?? undefined })
              }
              clearable
              placeholder="Select payer plan"
            />
          ))}

        {show96146 &&
          (readOnly ? (
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Payer name
              </Text>
              <Text size="sm">{metadata.payerName ?? '—'}</Text>
            </Stack>
          ) : (
            <TextInput
              label="Payer name"
              value={metadata.payerName ?? ''}
              error={validationErrors.payerName}
              onChange={(e) => patch({ payerName: e.currentTarget.value || undefined })}
              placeholder="e.g. BS Promise"
            />
          ))}

        {show96146 &&
          (readOnly ? (
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Prior 96146 count for client
              </Text>
              <Text size="sm">
                {metadata.prior96146CountForClient != null
                  ? String(metadata.prior96146CountForClient)
                  : '—'}
              </Text>
            </Stack>
          ) : (
            <NumberInput
              label="Prior 96146 count for client"
              value={metadata.prior96146CountForClient ?? ''}
              error={validationErrors.prior96146CountForClient}
              min={0}
              allowDecimal={false}
              onChange={(val) =>
                patch({
                  prior96146CountForClient:
                    typeof val === 'number' && Number.isFinite(val) ? val : undefined,
                })
              }
            />
          ))}
      </SimpleGrid>
    </Stack>
    </Box>
  );
}
